import { useCallback, useRef, useEffect, useMemo, useState } from "react";

import FuzzySearch from "fuzzy-search";

type BaseFuzzySearchArgs = {
  readonly searchKeys: string[];
  readonly enabled?: boolean;
  readonly searchOptions?: FuzzySearch.Options;
};

export type ControlledFuzzySearchArgs = BaseFuzzySearchArgs & {
  readonly search: string;
  readonly initialSearch?: never;
};

export type UncontrolledFuzzySearchArgs = BaseFuzzySearchArgs & {
  readonly search?: never;
  readonly initialSearch?: string;
};

export type FuzzySearchArgs =
  | ControlledFuzzySearchArgs
  | UncontrolledFuzzySearchArgs;

const isControlled = (
  args: FuzzySearchArgs,
): args is ControlledFuzzySearchArgs =>
  (args as ControlledFuzzySearchArgs).search !== undefined;

export type FuzzySearchControlledReturnType = {
  search?: never;
  setSearch?: never;
  clearSearch?: never;
  type: "controlled";
  enabled: boolean;
};

export type FuzzySearchUncontrolledReturnType = {
  search: string;
  setSearch: (value: string) => void;
  clearSearch: () => void;
  type: "uncontrolled";
  enabled: boolean;
};

export type FuzzySearchReturnType<
  ARGS extends FuzzySearchArgs,
  O extends object | string,
> = [
  O[],
  ARGS extends ControlledFuzzySearchArgs
    ? FuzzySearchControlledReturnType
    : FuzzySearchUncontrolledReturnType,
];

export const useFuzzySearch = <
  ARGS extends FuzzySearchArgs,
  O extends object | string,
>(
  options: O[],
  config: ARGS,
): FuzzySearchReturnType<ARGS, O> => {
  const lastIsControlled = useRef<boolean | null>(null);

  useEffect(() => {
    const controlled = isControlled(config);
    if (lastIsControlled.current !== null) {
      if (lastIsControlled.current === false && controlled === true) {
        throw new Error(
          "Search behavior cannot transition to a controlled state from an uncontrolled one.",
        );
      } else if (lastIsControlled.current === true && controlled === false) {
        throw new Error(
          "Search behavior cannot transition to an uncontrolled state from a controlled one.",
        );
      }
    }
    lastIsControlled.current = controlled;
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [config.search]);

  const [_search, _setSearch] = useState(
    /* If the search is controlled, the initial state does not matter because the state will not be
       used. */
    isControlled(config) ? "" : config.initialSearch || "",
  );
  /* If the hook is being used in a controlled sense (i.e. the search value is controlled externally
     to the component), favor the externally provided value. */
  const search = isControlled(config) ? config.search : _search;

  const setSearch = useCallback(
    (v: string) => {
      if (isControlled(config)) {
        throw new Error(
          "Cannot set the search value when the search is controlled.",
        );
      }
      _setSearch(v);
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [config.search],
  );

  const clearSearch = useCallback(() => {
    setSearch("");
  }, [setSearch]);

  const searcher = useMemo(() => {
    return new FuzzySearch<O>(options, config.searchKeys, config.searchOptions);
  }, [config.searchKeys, options, config.searchOptions]);

  const filteredOptions = useMemo(() => {
    if (config.enabled !== false && search !== "") {
      return searcher.search(search);
    }
    return options;
  }, [options, search, searcher, config.enabled]);

  if (isControlled(config)) {
    return [
      filteredOptions,
      { enabled: config.enabled !== false, type: "controlled" },
    ] as FuzzySearchReturnType<ARGS, O>;
  }
  return [
    filteredOptions,
    {
      search,
      setSearch,
      clearSearch,
      enabled: config.enabled !== false,
      type: "uncontrolled",
    },
  ] as FuzzySearchReturnType<ARGS, O>;
};
